/**
 * Rewrites top-level blocksState entries for in-row sibling access using an
 * inner-first RowScope STACK. First-match-wins: for each child whose parent
 * Repeatable matches a stack level, the chained runtime key joins all outer
 * instance keys down to that level (separator `_`, locked invariant from
 * runTimeBlocks.ts). Stack [{inner:o1}, {outer:u1}] → match at level 0 ⇒ "u1_o1",
 * match at level 1 ⇒ "u1".
 *
 * The `instances` Proxy from attachInstances is preserved on each rewritten
 * entry; row-scoped method dispatchers are also attached so `form_1.submit()`
 * dispatches to the row's runtime instance.
 */

import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { BlockControlMethod } from '../../types/blockDefinition';
import { getRuntimeBlockId, RUNTIME_BLOCK_ID_SEPARATOR } from '../runTimeBlocks';
import type { RuntimeSourceInfo } from './attachInstances';
import { resolveBlockState } from './blockStateResolver';
import { buildRowScopedMethodDispatchers } from './buildRowScopedMethodDispatchers';

type BlocksState = Record<string, BlockStateUnionType | undefined>;

export interface RowScope {
  parentBlockId: string;
  instanceKey: string;
}

interface AttachRowScopeArgs {
  blocksState: BlocksState;
  runtimeSourceInfo: RuntimeSourceInfo;
  instanceLookupState?: BlocksState;
  rowScopeStack: readonly RowScope[];
  blockMethods?: Record<string, BlockControlMethod[] | undefined>;
  callMethod?: (params: {
    blockId: string;
    targetBlockId: string;
    methodName: string;
    methodPayload: unknown;
  }) => void;
}

const buildChainedKeyForMatch = (
  rowScopeStack: readonly RowScope[],
  matchedIndex: number,
): string => {
  // walk outer→inner: stack is inner-first, so iterate from end down to matchedIndex
  const segments: string[] = [];
  for (let level = rowScopeStack.length - 1; level >= matchedIndex; level -= 1) {
    segments.push(rowScopeStack[level].instanceKey);
  }
  return segments.join(RUNTIME_BLOCK_ID_SEPARATOR);
};

export function attachRowScopeIdentities({
  blocksState,
  runtimeSourceInfo,
  instanceLookupState,
  rowScopeStack,
  blockMethods,
  callMethod,
}: AttachRowScopeArgs): BlocksState {
  if (rowScopeStack.length === 0) return blocksState;
  const { childToRepeatable } = runtimeSourceInfo;
  const childBlockIds = Object.keys(childToRepeatable);
  if (childBlockIds.length === 0) return blocksState;

  // Defer the ~N-key shallow clone of blocksState until we know at least one
  // child actually matches the stack. Returning the input ref on the no-match
  // path preserves `canReuseBaseContext` identity (hot path on 600-block pages).
  let result: BlocksState | undefined;
  const lookupState = instanceLookupState ?? blocksState;
  const getBlockConfig = (id: string) => result?.[id] ?? blocksState[id];

  for (const childBlockId of childBlockIds) {
    const repeatableAncestorId = childToRepeatable[childBlockId];
    if (!repeatableAncestorId) continue;

    let matchedIndex = -1;
    for (let level = 0; level < rowScopeStack.length; level += 1) {
      if (rowScopeStack[level].parentBlockId === repeatableAncestorId) {
        matchedIndex = level;
        break;
      }
    }
    if (matchedIndex === -1) continue;

    const currentEntry = result?.[childBlockId] ?? blocksState[childBlockId];
    if (!currentEntry) continue;

    const chainedKey = buildChainedKeyForMatch(rowScopeStack, matchedIndex);
    const runtimeId = getRuntimeBlockId({
      originalBlockId: childBlockId,
      runtimeBlockKey: chainedKey,
    });
    const resolved = resolveBlockState(runtimeId, lookupState, getBlockConfig);
    if (!resolved) continue;

    // blockMethods runtime shape is { methodName: fn }, even though the type says array.
    const methodFns = blockMethods?.[childBlockId];
    const methodNames = methodFns && typeof methodFns === 'object' ? Object.keys(methodFns) : [];
    const rowScopedMethods =
      callMethod && methodNames.length > 0
        ? buildRowScopedMethodDispatchers({
            childBlockId,
            runtimeId,
            methodNames,
            callMethod,
          })
        : {};

    if (!result) result = { ...blocksState };

    // preserve the instances Proxy from attachInstances — still valid here for cross-row reads
    const preservedInstances = (currentEntry as { instances?: unknown }).instances;
    result[childBlockId] = {
      ...resolved,
      ...rowScopedMethods,
      ...(preservedInstances !== undefined ? { instances: preservedInstances } : {}),
    } as BlockStateUnionType;
  }

  return result ?? blocksState;
}
